import {
  Button,
  Divider,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import * as React from "react";
import {
  arraySum,
  fileNameFormat,
} from "../../../../common/services/Utilities";
import BranchOfficeDeposits from "../../../../containers/BranchOfficeDeposits";
import ResultPagination from "../../../../containers/ResultPagination";
import SelectedMarketTable from "../../../../containers/SelectedMarketTable";
import { KeyValue, Query, TableHeader } from "../../../../interfaces";
import {
  additionalHeaderColor,
  primaryHeaderColor,
  textHeaderColor,
} from "../../../../refdata/constants";
import ApiDataDownload, { apiDownloadProps } from "../../../ApiDataDownload";
import LoadingIndicator from "../../../LoadingIndicator";
import ModalDialog from "../../../ModalDialog";
import MultiHeaderTable from "../../../MultiHeaderTable/MultiHeaderTable";
import {
  getStateName,
  selectedStatesAlpha,
} from "../../../../refdata/FilterOptions";

const useStyles = makeStyles(theme => ({
  bhcCellStyle: {
    "backgroundColor": "#acc7df",
    "fontWeight": "bold",
    "&:hover td, &:focus-visible td": {
      backgroundColor: "#C6EDF2",
    },
  },
  bhcRowStyle: {
    "&:hover td, &:focus-visible td": {
      backgroundColor: "#C6EDF2",
      cursor: "pointer",
    },
  },
  heading: {
    color: theme.palette.primary.main,
    width: 700,
    minWidth: 700,
    fontSize: 26,
    fontWeight: "bold",
    whiteSpace: "nowrap",
    paddingBottom: "1rem",
  },
  firstCellDarken: {
    backgroundColor: "rgba(247, 247, 247, 1)",
    textAlign: "left",
  },
  firstCellDarkest: {
    backgroundColor: "rgba(242, 242, 242, 1)",
    textAlign: "left",
  },
  firstCellLight: {
    backgroundColor: "rgba(250, 250, 250, 1)",
    textAlign: "left",
  },
  firstCellLightest: {
    backgroundColor: "rgba(255, 255, 255, 1)",
    textAlign: "left",
  },
  footer: {
    color: theme.palette.primary.main,
    lineHeight: "24px",
    fontSize: "16px",
    fontWeight: 400,
    textAlign: "left",
  },
  footerText: {
    color: theme.palette.primary.main,
    width: 700,
    minWidth: 700,
    fontSize: 16,
    whiteSpace: "nowrap",
  },
  holdingCompanyTitleStyle: {
    color: theme.palette.primary.main,
    width: 700,
    minWidth: 700,
    fontSize: 26,
    fontWeight: "bold",
    whiteSpace: "nowrap",
    paddingBottom: "1rem",
  },
  viewResultsButton: {
    "&.MuiButton-root": {
      "width": 115,
      "margin": "0px 8px",
      "float": "left",
      "border": `1px solid ${theme.palette.primary.main}`,
      "&.MuiButton-containedSecondary": {
        backgroundColor: "white",
        border: `1px solid ${theme.palette.primary.light}`,
      },
      "&:hover, &.Mui-focusVisible": {
        color: "#ffffff",
        border: `1px solid ${theme.palette.primary.main}`,

        backgroundColor: theme.palette.primary.main,
      },
      "&.Mui-disabled": {
        backgroundColor: "#dbdbdb",
        borderColor: "#bfcbd8",
        color: "rgba(0, 0, 0, 0.4)",
      },
    },
  },
  buttonLink: {
    "fontWeight": "bold",
    "color": "#0000FF",
    "padding": 0,
    "marginTop": 0,
    "fontSize": "1rem",
    "&.MuiButton-root:hover, &.MuiButton-root:focus-visible": {
      backgroundColor: "#fff",
      textDecoration: "underline",
    },
  },
  groupHover: {
    "&.MuiTableCell-root.MuiTableCell-body": {
      backgroundColor: theme.palette.secondary.main,
      cursor: "pointer",
    },
  },
  loadingSpinner: {
    marginTop: 24,
    width: "100%",
    textAlign: "center",
  },
  loadingText: {
    fontSize: 20,
    width: "100%",
    textAlign: "center",
  },
}));

interface Props {
  appState: Query;
  searchPills: React.ReactElement;
  tableData: KeyValue[];
  tableDataTotals: KeyValue[];
  tableComponent: string;
  loading: boolean;
  updatePagination?: () => void;
  subData?: KeyValue;
  outsideSelectedMarketInstitutionData?: KeyValue;
  noBHCData?: KeyValue[];
}

export default function DepositMarketTables({
  appState,
  searchPills,
  tableData,
  tableDataTotals,
  tableComponent,
  loading,
  subData,
  updatePagination,
  outsideSelectedMarketInstitutionData,
  noBHCData,
}: Props) {
  const [tableDataExists, setTableDataExists] = React.useState(
    tableData[0]?.sum_DEPSUMBR,
  );
  const classes = useStyles();
  const [groupHover, setGroupHover] = React.useState("");
  const [open, setOpen] = React.useState("");
  const [name, setName] = React.useState("");
  const [withBhc, setWithBhc] = React.useState(false);
  React.useEffect(() => {
    if (tableDataExists !== tableData[0]?.sum_DEPSUMBR) {
      setTableDataExists(tableData[0]?.sum_DEPSUMBR);
      setWithBhc(Boolean(tableData[0]?.sum_DEPSUMBR));
    }
  }, [tableData, tableDataExists]);

  const branchOfficeModal = (
    <ModalDialog
      title={name}
      display={open !== ""}
      setDisplay={() => setOpen("")}
      maxWidth="lg"
    >
      <BranchOfficeDeposits
        providedAppState={{
          ...appState,
          ui: {
            ...appState.ui,
            institutionType:
              appState.ui.institutionType === "bhcs" && withBhc
                ? "bhcs"
                : "banks",
          },
          filters: {
            ...appState.filters,
            cert: appState.ui.institutionType === "bhcs" && withBhc ? "" : open,
            bhcRSSD:
              appState.ui.institutionType === "bhcs" && withBhc ? open : "",
          },
        }}
      />
    </ModalDialog>
  );
  const merged = [...tableData, ...tableDataTotals];
  const tableDownloadHeaders =
    appState.ui.institutionType !== "bhcs"
      ? [
          "Reporting Period",
          "State (HQ)",
          "County",
          "Institution Name",
          "CERT",
          "Matching Office Count",
          "Matching Total Deposits (Thousands USD)",
          "Market Share",
          "Outside Market Office Counts",
          "Outside Market Total Deposits (Thousands USD)",
          "Bank Class",
          "Charter",
        ]
      : [
          "Reporting Period",
          "State (HQ)",
          "Bank Holding Company",
          "RSSDHCR",
          "Matching Office Count",
          "Total Combined Deposits (Thousands USD)",
          "Market Share",
          "Outside Market Office Count",
          "Outside Market Total Deposits (Thousands USD)",
        ];

  const tableDownloadHeadersNoBhc = [
    "Reporting Period",
    "State (HQ)",
    "Institution Name",
    "CERT",
    "Matching Office Count",
    "Matching Total Deposits (Thousands USD)",
    "Market Share",
    "Outside Market Office Counts",
    "Outside Market Total Deposits (Thousands USD)",
    "Bank Class",
    "Charter",
  ];

  const tableDownloadHeadersTotalOrg = [
    "Scope",
    "Matching Office Count",
    "Total Combined Deposits (Thousands USD)",
    "Market Share",
    "Outside Market Office Count",
    "Outside Market Total Deposits (Thousands USD)",
  ];

  const noBHCTotals =
    noBHCData && noBHCData[0] && Object.keys(noBHCData[0]).length
      ? arraySum(noBHCData)
      : { count: 0, sum_DEPSUMBR: 0, sum_DEPSUMBR_out: 0, count_out: 0 };
  const bhcPercentage =
    noBHCTotals && appState.ui.institutionType === "bhcs"
      ? Number(tableDataTotals[0].sum_DEPSUMBR) /
        Number(noBHCTotals.sum_DEPSUMBR + tableDataTotals[0].sum_DEPSUMBR)
      : 1;

  const selectedMarkets: string[][] = [];

  if (
    appState.filters.stateNames?.[0] &&
    !appState.filters.msa &&
    !appState.filters.zipbr
  ) {
    for (let i = 0; i < appState.filters.stateNames.length; i++) {
      const market = [
        getStateName(selectedStatesAlpha, appState.filters.stateNames[i]),
        appState.ui.marketSelection[i]?.counties?.join(", "),
        appState.ui.marketSelection[i]?.cities?.join(", "),
        appState.ui.marketSelection[i]?.zips?.join(", "),
      ];
      selectedMarkets.push(market);
    }
  }

  const totalBankOrg: any = [];
  const scopeTotalsArray: string[] = [
    "BHCs and Subsidiaries",
    "Institutions without BHC",
    "Totals",
  ];
  const bankOrgCount: any[] = [
    tableDataTotals[0].count,
    noBHCTotals?.count,
    noBHCTotals?.count + tableDataTotals[0].count,
  ];
  const bankOrgTotalDep: any[] = [
    tableDataTotals[0].sum_DEPSUMBR,
    noBHCTotals?.sum_DEPSUMBR,
    noBHCTotals?.sum_DEPSUMBR + tableDataTotals[0].sum_DEPSUMBR,
  ];
  const bankOrgMarketShare = [
    tableDataTotals[0].sum_DEPSUMBR && noBHCTotals?.sum_DEPSUMBR !== undefined
      ? `${(bhcPercentage * 100).toFixed(2)}%`
      : "0.00%",
    tableDataTotals[0].sum_DEPSUMBR && noBHCTotals?.sum_DEPSUMBR
      ? `${((1 - bhcPercentage) * 100).toFixed(2)}%`
      : "0.00%",
    "100.00%",
  ];

  const out_count_totals = tableDataTotals[0].count_out;

  const out_dep_totals = tableDataTotals[0].sum_DEPSUMBR_out;

  const bankOrgOutCount = [
    out_count_totals,
    noBHCTotals?.count_out,
    noBHCTotals?.count_out + out_count_totals,
  ];
  const bankOrgOutDep = [
    out_dep_totals,
    noBHCTotals?.sum_DEPSUMBR_out,
    noBHCTotals?.sum_DEPSUMBR_out + out_dep_totals,
  ];

  for (let i = 0; i < scopeTotalsArray.length; i++) {
    const bankOrg = [
      scopeTotalsArray[i],
      bankOrgCount[i]?.toLocaleString(),
      bankOrgTotalDep[i]?.toLocaleString(),
      bankOrgMarketShare[i],
      bankOrgOutCount[i]?.toLocaleString(),
      bankOrgOutDep[i]?.toLocaleString(),
    ];
    totalBankOrg.push(bankOrg);
  }

  const noBHCDownloadTotals = [
    `6/30/${appState.filters.lastYear}`,
    "",
    "Totals",
    "",
    ...totalBankOrg[1].slice(1),
  ];

  const selectedMarket =
    appState.filters.stateNames?.[0] &&
    !appState.filters.msa &&
    !appState.filters.zipbr
      ? [
          ["Selected Market"],
          ["State", "Counties", "Cities", "ZIP Codes"],
          ...selectedMarkets,
          [],
        ]
      : [];
  const tableDownloadData: KeyValue[] = merged.map((item: KeyValue) => {
    return [
      `6/30/${appState.filters.lastYear}`,
      item.STALP,
      item.CNTYNAMB,
      item.NAMEFULL ?? "Totals",
      item.CERT,
      Number(item.count).toLocaleString(),
      Number(item.sum_DEPSUMBR).toLocaleString(),
      `${item.mkt?.toFixed(2)}%`,
      Number(item.count_out).toLocaleString(),
      Number(item.sum_DEPSUMBR_out).toLocaleString(),
      item.BKCLASS,
      item.CHARTER,
    ];
  });

  const tableDownloadDataNoBHCs: KeyValue[] = noBHCData
    ? noBHCData.map((item: KeyValue) => {
        const mktVal = item.mkt
          ? `${(item.mkt * (1 - bhcPercentage))?.toFixed(2)}%`
          : "0.00%";

        return [
          `6/30/${appState.filters.lastYear}`,
          item.STALP,
          item.NAMEFULL ?? "Totals",
          item.CERT,
          Number(item.count).toLocaleString(),
          Number(item.sum_DEPSUMBR).toLocaleString(),
          mktVal,
          Number(item.count_out).toLocaleString(),
          Number(item.sum_DEPSUMBR_out).toLocaleString(),
          item.BKCLASS,
          item.CHARTER,
        ];
      })
    : [];
  const tableDownloadDataBHCs: KeyValue[] = merged.map((item: KeyValue) => {
    const mktVal = item.mkt
      ? `${(item.mkt * bhcPercentage)?.toFixed(2)}%`
      : "0.00%";
    return [
      `6/30/${appState.filters.lastYear}`,
      item.STALPHCR,
      item.NAMEHCR ?? "Totals",
      item.RSSDHCR,
      Number(item.count).toLocaleString(),
      Number(item.sum_DEPSUMBR).toLocaleString(),
      mktVal,
      Number(item.count_out).toLocaleString(),
      Number(item.sum_DEPSUMBR_out).toLocaleString(),
    ];
  });
  const combinedData = (noMarket?: boolean) => {
    const bhcHeader = noMarket
      ? [[], ["Holding Companies in Market"]]
      : selectedMarket;

    const header = appState.ui.institutionType === "bhcs" ? bhcHeader : [];
    return [
      ...header,
      tableDownloadHeaders,
      ...(appState.ui.institutionType !== "bhcs"
        ? tableDownloadData
        : tableDownloadDataBHCs),
    ];
  };
  const combinedDataNoBhc = (noMarket?: boolean) => {
    const header = noMarket
      ? [[], ["Institutions without Holding Companies in Market"]]
      : selectedMarket;
    return [
      ...header,
      tableDownloadHeadersNoBhc,
      ...tableDownloadDataNoBHCs,
      noBHCDownloadTotals,
    ];
  };
  const combinedDataTotal = (noMarket?: boolean) => {
    const header = noMarket
      ? [["Total Bank Organizations in Market"]]
      : selectedMarket;
    return [...header, tableDownloadHeadersTotalOrg, ...totalBankOrg];
  };

  const importantInfoLink =
    appState.filters.bhcRSSD || appState.ui.institutionType === "bhcs" ? (
      <Button
        className={classes.buttonLink}
        onClick={() => window.scrollTo(0, document.body.scrollHeight)}
      >
        Click for important information about this data.
      </Button>
    ) : (
      <></>
    );

  const holdingCompanyTitle = noBHCData && (
    <>
      <br />
      <br />
      <Typography className={classes.holdingCompanyTitleStyle}>
        <b>
          {withBhc
            ? `Holding Companies with Subsidiaries in Market: ${tableData.length.toLocaleString()}`
            : `Institutions without Holding Companies in Market: ${noBHCData.length.toLocaleString()}`}
        </b>
      </Typography>
    </>
  );
  const orgTitle = noBHCData ? (
    <>
      <br />
      <br />
      <Typography className={classes.holdingCompanyTitleStyle}>
        <b>
          Total Bank Organizations in Market:{" "}
          {loading
            ? ""
            : (tableData.length + noBHCData.length).toLocaleString()}
        </b>
      </Typography>
    </>
  ) : (
    <></>
  );

  const tableCellProps = (cert: string, name: string) => {
    const onClickFn = (e: KeyValue) => {
      setName(name);
      if (e.key && e.key !== "Enter") {
        return;
      }
      if (
        appState.filters.msa ||
        (appState.filters.stateNames && appState.filters.lastYear)
      ) {
        setOpen(cert);
      }
    };
    return {
      onMouseEnter: () => setGroupHover(cert),
      onMouseLeave: () => setGroupHover(""),
      onFocus: () => setGroupHover(cert),
      onBlur: () => setGroupHover(""),
      onClick: onClickFn,
      onKeyDown: onClickFn,
      tabIndex: 0,
    };
  };

  React.useEffect(() => {
    if (appState.ui.institutionType !== "bhcs") {
      setWithBhc(true);
    }
  }, [appState.ui.institutionType]);

  const paginationDataValues = withBhc ? tableData : noBHCData || [];

  const paginatedData = paginationDataValues.slice(
    (appState.pagination.pageNumber - 1) * appState.pagination.resultLimit,
    appState.pagination.resultLimit * appState.pagination.pageNumber,
  );

  const totalOrgTable: React.ReactElement[] = noBHCTotals
    ? [
        <React.Fragment key={"noBHCTotals"}>
          <TableRow>
            <TableCell colSpan={1}>
              <Button
                id="viewBhcTable"
                color={withBhc ? "primary" : "secondary"}
                variant="contained"
                className={classes.viewResultsButton}
                disabled={!tableDataExists}
                disableElevation
                onClick={() => {
                  setWithBhc(true);
                  updatePagination && updatePagination();
                }}
              >
                View Results
              </Button>
            </TableCell>
            <TableCell colSpan={1} style={{ textAlign: "left" }}>
              BHCs and Subsidiaries
            </TableCell>
            <TableCell colSpan={1}>
              {tableDataTotals[0].count?.toLocaleString()}
            </TableCell>
            <TableCell colSpan={1} style={{ textAlign: "right" }}>
              {tableDataTotals[0].sum_DEPSUMBR?.toLocaleString()}
            </TableCell>
            <TableCell colSpan={1}>
              {tableDataTotals[0].sum_DEPSUMBR &&
              noBHCTotals.sum_DEPSUMBR !== undefined
                ? `${(bhcPercentage * 100).toFixed(2)}%`
                : "0.00%"}
            </TableCell>
            <TableCell colSpan={1}>
              {tableDataTotals[0].count_out?.toLocaleString()}
            </TableCell>
            <TableCell colSpan={1} style={{ textAlign: "right" }}>
              {tableDataTotals[0].sum_DEPSUMBR_out?.toLocaleString()}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell colSpan={1}>
              <Button
                id="viewNoBhcTable"
                color={!withBhc ? "primary" : "secondary"}
                variant="contained"
                className={classes.viewResultsButton}
                disableElevation
                disabled={!noBHCTotals.count}
                onClick={() => {
                  setWithBhc(false);
                  updatePagination && updatePagination();
                }}
              >
                View Results
              </Button>
            </TableCell>
            <TableCell colSpan={1} style={{ textAlign: "left" }}>
              Institutions without BHC
            </TableCell>
            <TableCell colSpan={1}>
              {noBHCTotals.count?.toLocaleString()}
            </TableCell>
            <TableCell colSpan={1} style={{ textAlign: "right" }}>
              {noBHCTotals.sum_DEPSUMBR?.toLocaleString()}
            </TableCell>
            <TableCell colSpan={1}>
              {tableDataTotals[0].sum_DEPSUMBR || noBHCTotals.sum_DEPSUMBR
                ? `${((1 - bhcPercentage) * 100).toFixed(2)}%`
                : "0.00%"}
            </TableCell>
            <TableCell colSpan={1}>
              {noBHCTotals.count_out?.toLocaleString()}
            </TableCell>
            <TableCell colSpan={1} style={{ textAlign: "right" }}>
              {noBHCTotals.sum_DEPSUMBR_out?.toLocaleString()}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell
              colSpan={2}
              className={classes.firstCellDarkest}
              style={{
                textAlign: "left",
                backgroundColor: primaryHeaderColor,
                color: textHeaderColor,
                fontWeight: "bold",
              }}
            >
              Totals
            </TableCell>
            <TableCell
              colSpan={1}
              className={classes.firstCellDarkest}
              style={{
                backgroundColor: additionalHeaderColor,
                color: textHeaderColor,
                fontWeight: "bold",
              }}
            >
              {(noBHCTotals.count + tableDataTotals[0].count).toLocaleString()}
            </TableCell>
            <TableCell
              style={{
                textAlign: "right",
                backgroundColor: additionalHeaderColor,
                color: textHeaderColor,
                fontWeight: "bold",
              }}
            >
              {(
                noBHCTotals.sum_DEPSUMBR + tableDataTotals[0].sum_DEPSUMBR
              )?.toLocaleString()}
            </TableCell>
            <TableCell
              style={{
                textAlign: "center",
                backgroundColor: additionalHeaderColor,
                color: textHeaderColor,
                fontWeight: "bold",
              }}
            >
              100.00%
            </TableCell>
            <TableCell
              colSpan={1}
              className={classes.firstCellDarkest}
              style={{
                textAlign: "center",
                backgroundColor: "#4c76b7",
                color: textHeaderColor,
                fontWeight: "bold",
              }}
            >
              {(noBHCTotals.count_out + out_count_totals)?.toLocaleString()}
            </TableCell>
            <TableCell
              colSpan={1}
              className={classes.firstCellDarkest}
              style={{
                backgroundColor: "#4c76b7",
                textAlign: "right",
                color: textHeaderColor,
                fontWeight: "bold",
              }}
            >
              {(
                noBHCTotals.sum_DEPSUMBR_out + out_dep_totals
              )?.toLocaleString()}
            </TableCell>
          </TableRow>
        </React.Fragment>,
      ]
    : [<></>];

  const table: React.ReactElement[] =
    paginationDataValues.length > 0
      ? paginatedData.map((data: KeyValue, int: number) => {
          const lightCell =
            int % 2 !== 0 ? classes.firstCellLightest : classes.firstCellLight;
          const darkCell =
            int % 2 !== 0 ? classes.firstCellDarken : classes.firstCellDarkest;
          const id =
            appState.ui.institutionType === "bhcs" && withBhc
              ? data.RSSDHCR
              : data.CERT;
          const name =
            appState.ui.institutionType === "bhcs" && withBhc
              ? data.NAMEHCR
              : data.NAMEFULL;
          const darkHoverClass =
            groupHover === id ? classes.groupHover : darkCell;
          const lightHoverClass =
            groupHover === id ? classes.groupHover : lightCell;
          return (
            <React.Fragment key={`${id}_${int}`}>
              <TableRow
                id={id}
                className={
                  data.RSSDHCR && appState.ui.institutionType !== "banks"
                    ? classes.bhcRowStyle
                    : ""
                }
              >
                <TableCell
                  {...tableCellProps(id, name)}
                  colSpan={1}
                  className={
                    data.RSSDHCR && appState.ui.institutionType !== "banks"
                      ? classes.bhcCellStyle
                      : darkHoverClass
                  }
                  style={{
                    textAlign: "left",
                  }}
                >
                  {name}
                </TableCell>
                <TableCell
                  {...tableCellProps(id, name)}
                  colSpan={1}
                  className={
                    data.RSSDHCR && appState.ui.institutionType !== "banks"
                      ? classes.bhcCellStyle
                      : darkHoverClass
                  }
                >
                  {id}
                </TableCell>
                <TableCell
                  {...tableCellProps(id, name)}
                  colSpan={1}
                  className={
                    data.RSSDHCR && appState.ui.institutionType !== "banks"
                      ? classes.bhcCellStyle
                      : lightHoverClass
                  }
                  style={{
                    color:
                      data.RSSDHCR &&
                      appState.ui.institutionType !== "banks" &&
                      "#1265ff",
                    textDecoration:
                      data.RSSDHCR &&
                      appState.ui.institutionType !== "banks" &&
                      "underline",
                  }}
                >
                  {data.count?.toLocaleString()}
                </TableCell>
                <TableCell
                  {...tableCellProps(id, name)}
                  colSpan={1}
                  className={
                    data.RSSDHCR && appState.ui.institutionType !== "banks"
                      ? classes.bhcCellStyle
                      : lightHoverClass
                  }
                  style={{
                    textAlign: "right",
                  }}
                >
                  {data.sum_DEPSUMBR?.toLocaleString()}
                </TableCell>
                <TableCell
                  {...tableCellProps(id, name)}
                  colSpan={1}
                  className={
                    data.RSSDHCR && appState.ui.institutionType !== "banks"
                      ? classes.bhcCellStyle
                      : lightHoverClass
                  }
                >
                  {(
                    Number(data.mkt) *
                    (withBhc ? bhcPercentage : 1 - bhcPercentage)
                  ).toFixed(2)}
                  %
                </TableCell>
                <TableCell
                  {...tableCellProps(id, name)}
                  colSpan={1}
                  className={
                    data.RSSDHCR && appState.ui.institutionType !== "banks"
                      ? classes.bhcCellStyle
                      : darkHoverClass
                  }
                >
                  {data.count_out?.toLocaleString()}
                </TableCell>
                <TableCell
                  {...tableCellProps(id, name)}
                  colSpan={1}
                  className={
                    data.RSSDHCR && appState.ui.institutionType !== "banks"
                      ? classes.bhcCellStyle
                      : darkHoverClass
                  }
                  style={{
                    textAlign: "right",
                  }}
                >
                  {data.sum_DEPSUMBR_out?.toLocaleString()}
                </TableCell>
                <TableCell
                  {...tableCellProps(id, name)}
                  colSpan={1}
                  className={
                    data.RSSDHCR && appState.ui.institutionType !== "banks"
                      ? classes.bhcCellStyle
                      : lightHoverClass
                  }
                >
                  {appState.ui.institutionType === "bhcs" && withBhc
                    ? ""
                    : data.BKCLASS}
                </TableCell>
                <TableCell
                  {...tableCellProps(id, name)}
                  colSpan={1}
                  className={
                    data.RSSDHCR && appState.ui.institutionType !== "banks"
                      ? classes.bhcCellStyle
                      : lightHoverClass
                  }
                >
                  {appState.ui.institutionType === "bhcs" && withBhc
                    ? ""
                    : data.CHARTER === "FED"
                    ? "Federal"
                    : "State"}
                </TableCell>
              </TableRow>
              {appState.ui.institutionType === "bhcs" &&
                data.institutions &&
                subData &&
                outsideSelectedMarketInstitutionData &&
                !loading &&
                data.institutions.map(
                  (inst: KeyValue, int: number) =>
                    subData.hasOwnProperty(inst.CERT) && (
                      <TableRow id={inst.CERT} key={`${inst.CERT}_${int}`}>
                        <TableCell
                          style={{
                            textAlign: "left",
                            paddingLeft: "35px",
                          }}
                        >
                          {inst?.NAMEFULL}
                        </TableCell>
                        <TableCell>{inst.CERT}</TableCell>
                        <TableCell>
                          {subData[inst.CERT].count?.toLocaleString()}
                        </TableCell>
                        <TableCell style={{ textAlign: "right" }}>
                          {subData[inst.CERT].sum_DEPSUMBR?.toLocaleString()}{" "}
                        </TableCell>
                        <TableCell>
                          {data.sum_DEPSUMBR
                            ? (
                                (Number(data.mkt) *
                                  (withBhc
                                    ? bhcPercentage
                                    : 1 - bhcPercentage) *
                                  subData[inst.CERT].sum_DEPSUMBR) /
                                data.sum_DEPSUMBR
                              ).toFixed(2)
                            : "0.00"}
                          %
                        </TableCell>
                        <TableCell>
                          {outsideSelectedMarketInstitutionData.hasOwnProperty(
                            inst.CERT,
                          )
                            ? outsideSelectedMarketInstitutionData[
                                inst.CERT
                              ].count?.toLocaleString()
                            : 0}
                        </TableCell>
                        <TableCell
                          style={{
                            textAlign: "right",
                          }}
                        >
                          {outsideSelectedMarketInstitutionData.hasOwnProperty(
                            inst.CERT,
                          )
                            ? outsideSelectedMarketInstitutionData[
                                inst.CERT
                              ].sum_DEPSUMBR?.toLocaleString()
                            : 0}
                        </TableCell>
                        <TableCell>{inst.BKCLASS}</TableCell>
                        <TableCell>
                          {inst.CHARTER === "FED" ? "Federal" : "State"}
                        </TableCell>
                      </TableRow>
                    ),
                )}
            </React.Fragment>
          );
        })
      : [
          <tr key="no-results">
            <td colSpan={7}>
              Double check your search criteria for errors and try your search
              again.
            </td>
          </tr>,
        ];
  const tableTotals: React.ReactElement[] = tableDataTotals.map(
    (data: KeyValue, int: number) => (
      <TableRow id={"market_share_totals"} key={`market_share_totals_${int}`}>
        <TableCell
          colSpan={2}
          className={
            int % 2 !== 0 ? classes.firstCellDarken : classes.firstCellDarkest
          }
          style={{
            textAlign: "left",
            backgroundColor: primaryHeaderColor,
            color: textHeaderColor,
            fontWeight: "bold",
          }}
        >
          {appState.ui.institutionType === "bhcs" && withBhc
            ? `Holding Companies in Market: ${tableData.length}`
            : "Totals"}
        </TableCell>
        <TableCell
          colSpan={1}
          className={
            int % 2 !== 0 ? classes.firstCellDarken : classes.firstCellDarkest
          }
          style={{
            backgroundColor: additionalHeaderColor,
            color: textHeaderColor,
            fontWeight: "bold",
          }}
        >
          {withBhc
            ? data.count?.toLocaleString()
            : noBHCTotals?.count?.toLocaleString()}
        </TableCell>
        <TableCell
          style={{
            textAlign: "right",
            backgroundColor: additionalHeaderColor,
            color: textHeaderColor,
            fontWeight: "bold",
          }}
        >
          {withBhc
            ? data.sum_DEPSUMBR?.toLocaleString()
            : noBHCTotals?.sum_DEPSUMBR?.toLocaleString()}
        </TableCell>
        <TableCell
          style={{
            textAlign: "center",
            backgroundColor: additionalHeaderColor,
            color: textHeaderColor,
            fontWeight: "bold",
          }}
        >
          {withBhc
            ? appState.ui.institutionType === "banks"
              ? "100.00%"
              : `${(bhcPercentage * 100).toFixed(2)}%`
            : `${((1 - bhcPercentage) * 100).toFixed(2)}%`}
        </TableCell>
        <TableCell
          colSpan={1}
          className={
            int % 2 !== 0 ? classes.firstCellDarken : classes.firstCellDarkest
          }
          style={{
            textAlign: "center",
            backgroundColor: "#4c76b7",
            color: textHeaderColor,
            fontWeight: "bold",
          }}
        >
          {withBhc
            ? out_count_totals?.toLocaleString()
            : noBHCTotals?.count_out?.toLocaleString()}
        </TableCell>
        <TableCell
          colSpan={1}
          className={
            int % 2 !== 0 ? classes.firstCellDarken : classes.firstCellDarkest
          }
          style={{
            backgroundColor: "#4c76b7",
            textAlign: "right",
            color: textHeaderColor,
            fontWeight: "bold",
          }}
        >
          {withBhc
            ? out_dep_totals?.toLocaleString()
            : noBHCTotals?.sum_DEPSUMBR_out?.toLocaleString()}
        </TableCell>
        <TableCell
          colSpan={2}
          className={
            int % 2 !== 0 ? classes.firstCellDarken : classes.firstCellDarkest
          }
          style={{
            backgroundColor: primaryHeaderColor,
            color: textHeaderColor,
            fontWeight: "bold",
          }}
        ></TableCell>
      </TableRow>
    ),
  );

  const tableHeaders: TableHeader[][] = [
    [
      {
        title: " ",
        styles: {
          backgroundColor: primaryHeaderColor,
          color: textHeaderColor,
        },
        colSpan: 2,
      },
      {
        title: "Selected Market",
        styles: {
          backgroundColor: additionalHeaderColor,
          color: textHeaderColor,
        },
        colSpan: 3,
      },
      {
        title: "Outside Selected Market",
        styles: {
          backgroundColor: "#4c76b7",
          color: textHeaderColor,
        },
        colSpan: 2,
      },
      {
        title: "",
        styles: {
          backgroundColor: primaryHeaderColor,
          color: textHeaderColor,
        },
        colSpan: 2,
      },
    ],
    (() => {
      const headerValues = [
        { title: "Office Count" },
        {
          title:
            appState.ui.institutionType === "bhcs" && withBhc
              ? "Total Combined Deposits* (Thousands USD)"
              : "Total Deposits (Thousands USD)",
          styles: { width: 170, textAlign: "right" },
        },
      ];

      const headerValues2 = [
        { title: "Office Count" },
        {
          title: "Total Deposits (Thousands USD)",
          styles: { width: 170, textAlign: "right" },
        },
      ];

      const otherValues = [
        {
          title: "Bank Class",
          styles: {
            backgroundColor: primaryHeaderColor,
            color: textHeaderColor,
            textAlign: "center",
          },
          colSpan: 1,
          apiField: "",
        },
        {
          title: "Charter",
          styles: {
            backgroundColor: primaryHeaderColor,
            color: textHeaderColor,
            width: 60,
          },
          colSpan: 1,
          apiField: "",
        },
      ];

      const headingInstitColumn =
        appState.ui.institutionType === "bhcs" && withBhc
          ? "Holding Company / Institution Name"
          : "Institution Name";
      const certColumn =
        appState.ui.institutionType === "bhcs" && withBhc
          ? "RSSDHCR / CERT"
          : "CERT";

      const institutionHeader: TableHeader[] = [
        {
          title: headingInstitColumn,
          styles: {
            backgroundColor: primaryHeaderColor,
            color: textHeaderColor,
            textAlign: "left",
            minWidth: 240,
          },
          colSpan: 1,
          apiField: "",
        },
        {
          title: certColumn,
          styles: {
            backgroundColor: primaryHeaderColor,
            color: textHeaderColor,
            textAlign: "center",
          },
          colSpan: 1,
          apiField: "",
        },
      ];
      const selectedMarket: TableHeader[] = headerValues.map(
        (header: KeyValue) => {
          return {
            title: header.title,
            styles: {
              backgroundColor: additionalHeaderColor,
              color: textHeaderColor,
              ...header.styles,
            },
            colSpan: 1,
            apiField: "",
          };
        },
      );

      const marketShare: TableHeader[] = [
        {
          title: "Market Share (%)",
          styles: {
            backgroundColor: additionalHeaderColor,
            color: textHeaderColor,
          },
          colSpan: 1,
          apiField: "",
        },
      ];

      const outSideMarket: TableHeader[] = headerValues2.map(
        (header: KeyValue) => {
          return {
            title: header.title,
            styles: {
              backgroundColor: "#4c76b7",
              color: textHeaderColor,
              ...header.styles,
            },
            colSpan: 1,
            apiField: "",
          };
        },
      );
      const other: TableHeader[] = otherValues.map((header: KeyValue) => {
        return {
          title: header.title,
          styles: {
            backgroundColor: primaryHeaderColor,
            color: textHeaderColor,
            ...header.styles,
          },
          colSpan: 1,
          apiField: "",
        };
      });

      return [
        ...institutionHeader,
        ...selectedMarket,
        ...marketShare,
        ...outSideMarket,
        ...other,
      ];
    })(),
  ];

  const tableTitle: string = "Deposit Market Share Results";

  const downloadFileTitle: string =
    tableComponent && appState.filters.stateNames[0]
      ? `DepositMarketShare_Custom_Market_${appState.filters.stateNames}`.replace(
          /,/g,
          "_",
        )
      : `DepositMarketShare_MSA_${appState.filters.msa}`.replace(/,/g, "_");

  const footer = (
    <Typography className={classes.footer}>
      <b>Note about this data for BHCs or Bank Holding Companies:</b>
      <br />
      This search will return results for top regulatory bank holding companies
      and their FDIC-insured subsidiaries. The regulatory top holder is assigned
      by the Federal Reserve Board based on ownership and control percentages.
      Thrift bank holding company information is not available. Official bank
      holding company information can be found by visiting the website of the
      Federal Reserve System - National Information Center.
    </Typography>
  );

  const bhcTableHeaders: TableHeader[][] = [
    [
      {
        title: " ",
        styles: {
          backgroundColor: primaryHeaderColor,
          color: textHeaderColor,
        },
        colSpan: 2,
      },
      {
        title: "Selected Market",
        styles: {
          backgroundColor: additionalHeaderColor,
          color: textHeaderColor,
        },
        colSpan: 3,
      },
      {
        title: "Outside Selected Market",
        styles: {
          backgroundColor: "#4c76b7",
          color: textHeaderColor,
        },
        colSpan: 2,
      },
    ],
    (() => {
      const headerValues = [
        { title: "Office Count" },
        {
          title: "Total Deposits (Thousands USD)",
          styles: { width: 170, textAlign: "right" },
        },
      ];

      const headerValues2 = [
        { title: "Office Count" },
        {
          title: "Total Deposits (Thousands USD)",
          styles: { width: 170, textAlign: "right" },
        },
      ];

      const institutionHeader: TableHeader[] = [
        {
          title: "Table Filter",
          styles: {
            backgroundColor: primaryHeaderColor,
            color: textHeaderColor,
            textAlign: "left",
          },
          colSpan: 1,
          apiField: "",
        },
        {
          title: "Scope",
          styles: {
            backgroundColor: primaryHeaderColor,
            color: textHeaderColor,
            textAlign: "left",
          },
          colSpan: 1,
          apiField: "",
        },
      ];
      const selectedMarket: TableHeader[] = headerValues.map(
        (header: KeyValue) => {
          return {
            title: header.title,
            styles: {
              backgroundColor: additionalHeaderColor,
              color: textHeaderColor,
              ...header.styles,
            },
            colSpan: 1,
            apiField: "",
          };
        },
      );

      const marketShare: TableHeader[] = [
        {
          title: "Market Share (%)",
          styles: {
            backgroundColor: additionalHeaderColor,
            color: textHeaderColor,
          },
          colSpan: 1,
          apiField: "",
        },
      ];

      const outSideMarket: TableHeader[] = headerValues2.map(
        (header: KeyValue) => {
          return {
            title: header.title,
            styles: {
              backgroundColor: "#4c76b7",
              color: textHeaderColor,
              ...header.styles,
            },
            colSpan: 1,
            apiField: "",
          };
        },
      );

      return [
        ...institutionHeader,
        ...selectedMarket,
        ...marketShare,
        ...outSideMarket,
      ];
    })(),
  ];
  const fileNameBhc = appState.ui.institutionType === "bhcs" ? "_with_BHC" : "";

  const apiDataNoBhc: apiDownloadProps = {
    appState,
    fileName: `${fileNameFormat(downloadFileTitle).replace(
      /__/g,
      "_",
    )}_without_BHC.csv`,
    apiPath: "sod",
    text: "Download Results Data - Institutions without BHC",
    data: appState.filters.msa
      ? (combinedDataNoBhc(true) as string[][])
      : (combinedDataNoBhc() as string[][]),
  };

  const apiDataTotalOrg: apiDownloadProps = {
    appState,
    fileName: `${fileNameFormat(downloadFileTitle).replace(
      /__/g,
      "_",
    )}_total_org.csv`,
    apiPath: "sod",
    text: "Download Total Bank Organizations",
    data: appState.filters.msa
      ? (combinedDataTotal(true) as string[][])
      : (combinedDataTotal() as string[][]),
  };

  const apiData: apiDownloadProps = {
    appState,
    fileName: `${fileNameFormat(downloadFileTitle).replace(
      /__/g,
      "_",
    )}${fileNameBhc}.csv`,
    apiPath: "sod",
    text:
      appState.ui.institutionType === "bhcs"
        ? "Download Results Data - BHCs in Market"
        : "Download Results Data - Formatted",
    data: combinedData() as string[][],
  };

  const apiDataAll: apiDownloadProps[] = [
    {
      appState,
      fileName: `${fileNameFormat(downloadFileTitle).replace(
        /__/g,
        "_",
      )}${fileNameBhc}_ALL.csv`,
      apiPath: "sod",
      text: "Download All Results Data",
      data:
        appState.ui.institutionType === "bhcs"
          ? [
              ...selectedMarket,
              ...combinedDataTotal(true),
              ...combinedData(true),
              ...combinedDataNoBhc(true),
            ]
          : [...selectedMarket, ...combinedData(true)],
    },
  ];

  if (appState.ui.institutionType === "bhcs") {
    apiDataAll.push(apiDataTotalOrg);
    apiDataAll.push(apiData);
    apiDataAll.push(apiDataNoBhc);
  }
  return (
    <>
      <Typography className={classes.heading}>
        {tableTitle}
        {appState.ui.institutionType === "bhcs"
          ? ""
          : ` - ${tableData.length.toLocaleString()} Matching Results`}
      </Typography>
      <div style={{ position: "relative", right: 0, top: 5 }}>
        {<ApiDataDownload app="SOD" apiData={apiDataAll} />}
      </div>

      <Divider />
      {searchPills}
      {tableComponent === "territories" ? <SelectedMarketTable /> : <></>}
      {appState.ui.institutionType === "bhcs" ? (
        <>
          {orgTitle}
          <MultiHeaderTable
            tableBody={
              loading ? (
                <LoadingIndicator text="Loading" spinnerSize={32} table />
              ) : (
                <>{totalOrgTable}</>
              )
            }
            tableHeaders={bhcTableHeaders}
          />
        </>
      ) : (
        <></>
      )}
      {importantInfoLink}
      {appState.ui.institutionType === "bhcs" ? holdingCompanyTitle : <></>}
      {branchOfficeModal}
      <ResultPagination count={paginationDataValues.length}>
        <MultiHeaderTable
          tableBody={
            loading ? (
              <LoadingIndicator
                text="Loading"
                spinnerSize={32}
                textClass={classes.loadingText}
                spinnerClass={classes.loadingSpinner}
                table
              />
            ) : (
              <>
                {table}
                {tableTotals}
              </>
            )
          }
          tableHeaders={tableHeaders}
        />
      </ResultPagination>
      {appState.ui.institutionType === "bhcs" && withBhc && (
        <Typography className={classes.footerText}>
          *The total deposits for the Bank Holding Company is the combined Total
          Domestic Deposits of the underlying Insured Subsidiaries.
        </Typography>
      )}
      <br />
      {(appState.filters.bhcRSSD || appState.ui.institutionType === "bhcs") &&
        withBhc &&
        footer}
    </>
  );
}
